import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, PhoneCall, ShieldCheck, Star, Timer } from "lucide-react";
import { BUSINESS, IMAGES } from "@/lib/data";
import { scrollToHash } from "@/components/Navbar";

const LINES = ["PRECISION-GRADE", "APPLIANCE REPAIR", "FOR FINE HOMES"];

const STATS = [
  { icon: ShieldCheck, value: "90-DAY", label: "Repair warranty" },
  { icon: Star, value: "4.9 / 5", label: "240+ verified reviews" },
  { icon: Timer, value: "SAME-DAY", label: "Dispatch available" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen overflow-hidden hero-grid grain">
      {/* signature on-load shutter */}
      <motion.div
        className="fixed inset-0 z-[90] bg-[#0F1215] flex items-center justify-center pointer-events-none"
        initial={{ y: 0 }}
        animate={{ y: "-100%" }}
        transition={{ duration: 1.05, delay: 0.75, ease: [0.83, 0, 0.17, 1] }}
      >
        <motion.span
          className="font-mono text-xs tracking-[0.4em] text-[#E06A26] uppercase"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 1, 0] }}
          transition={{ duration: 0.9, times: [0, 0.3, 0.8, 1] }}
        >
          Thortek // Systems Online
        </motion.span>
      </motion.div>

      <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-[#E06A26]/10 blur-[140px] pointer-events-none" />

      <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-40 pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E06A26] mb-8"
            data-testid="hero-eyebrow"
          >
            {BUSINESS.name} · {BUSINESS.location}
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-[4.4rem]"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "text-[#E06A26]" : ""}`}
                  initial={{ y: "115%", rotate: 2 }}
                  animate={{ y: 0, rotate: 0 }}
                  transition={{ duration: 1, delay: 1.05 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.75, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[#94A0AC]"
          >
            Factory-trained technicians for Sub-Zero, Wolf, Viking and the world's finest appliances.
            Diagnosed to spec, repaired with authentic parts, calibrated before we leave — plus yearly
            maintenance plans that keep failures from ever happening.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.95, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-book"
              onClick={() => scrollToHash("#contact")}
              className="group bg-[#E06A26] hover:bg-[#F07A34] text-[#0F1215] font-semibold px-8 py-4 rounded-full flex items-center gap-3 transition-all duration-300 hover:shadow-[0_0_36px_rgba(224,106,38,0.5)]"
            >
              Book a Repair
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </button>
            <a
              data-testid="hero-cta-emergency-call"
              href={BUSINESS.phoneHref}
              className="group border border-white/15 hover:border-[#E06A26]/60 text-[#F4F2EE] px-8 py-4 rounded-full flex items-center gap-3 transition-colors duration-300"
            >
              <PhoneCall className="w-4 h-4 text-[#E06A26]" />
              <span className="font-mono text-sm tracking-wider">{BUSINESS.phone}</span>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.2, duration: 1 }}
            className="mt-16 grid grid-cols-3 gap-6 max-w-lg"
          >
            {STATS.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label.split(" ")[0].toLowerCase()}`}>
                <s.icon className="w-4 h-4 text-[#E06A26] mb-2" />
                <p className="font-mono text-sm font-semibold tracking-wider">{s.value}</p>
                <p className="text-xs text-[#606D7A] mt-1">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          className="lg:col-span-5 relative spotlight"
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.4, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="clip-frame overflow-hidden border border-white/10">
            <motion.img
              src={IMAGES.hero}
              alt="Technician performing precision repair on a built-in appliance"
              data-testid="hero-image"
              style={{ y: imgY, scale: 1.18 }}
              className="w-full h-[420px] lg:h-[560px] object-cover img-spot"
            />
          </div>
          <div className="absolute -bottom-5 -left-5 bg-[#171B20]/90 backdrop-blur-md border border-white/10 px-5 py-4 rounded-xl">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#606D7A]">Work order</p>
            <p className="font-mono text-sm text-[#E06A26] mt-1">#TK-2481 · Calibrated</p>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
